import React, { Suspense } from "react";
import { Navigate, Outlet, useLocation } from "react-router-dom";
import LoadingPanel from "../components/feedback/LoadingPanel";
import ErrorBoundary from "../components/common/ErrorBoundary";
import Layout from "../components/Layout/Layout";
import { useAppSelector } from "../hooks/useAppSelector";
import { useMsal } from "@azure/msal-react";
import { useDispatch } from "react-redux";
import { loginWithAad, authActions } from "../store";
import { silentRequest } from "../core/auth/helpers/authConfig";
import { jwtDecode } from "jwt-decode";
import { FEATURE_ROUTES, FULLSCREEN_ROUTES } from "../config/app.config";

const App = () => {
  const { isAuthenticated, isLoading } = useAppSelector((x) => x.auth);
  const { instance } = useMsal();
  const dispatch = useDispatch();
  const pendingRef = React.useRef(false);

  // Token expiration kontrolü
  const checkTokenExpiration = React.useCallback(() => {
    const token = localStorage.getItem("jwtToken");
    if (token && isAuthenticated) {
      try {
        // Try to decode as JWT first
        const decoded = jwtDecode(token);
        const now = Math.floor(Date.now() / 1000);

        if (decoded.exp && decoded.exp < now) {
          // Token süresi dolmuş - logout yap
          const aadToken = localStorage.getItem("aadToken");
          if (aadToken) {
            instance.logoutRedirect({ onRedirectNavigate: () => false });
          }
          dispatch(authActions.performLogout());
        }
      } catch (jwtError) {
        // Not a JWT, try as offline token (base64 JSON)
        try {
          const decoded = JSON.parse(atob(token));
          const now = Math.floor(Date.now() / 1000);

          if (decoded.exp && decoded.exp < now) {
            dispatch(authActions.performLogout());
          }
        } catch (offlineError) {
          // Invalid token format
          dispatch(authActions.performLogout());
        }
      }
    }
  }, [isAuthenticated, dispatch, instance]);

  // Periyodik token kontrolü (her 30 saniyede)
  React.useEffect(() => {
    if (isAuthenticated) {
      checkTokenExpiration(); // İlk kontrol
      const interval = setInterval(checkTokenExpiration, 30000); // 30 saniye
      return () => clearInterval(interval);
    }
  }, [isAuthenticated, checkTokenExpiration]);

  // Sayfa focus olduğunda token kontrolü
  React.useEffect(() => {
    const handleFocus = () => {
      if (isAuthenticated) {
        checkTokenExpiration();
      }
    };

    window.addEventListener("focus", handleFocus);
    return () => window.removeEventListener("focus", handleFocus);
  }, [isAuthenticated, checkTokenExpiration]);

  React.useEffect(() => {
    // Sadece MSAL authenticated ama Redux state authenticated değilse token al
    // (Yani kullanıcı MSAL ile giriş yapmış ama henüz backend'e istek atmamış)
    const hasJwtToken = localStorage.getItem("jwtToken");
    const hasMsalAccount = instance.getAllAccounts().length > 0;

    // Offline token varsa MSAL refresh yapma
    if (hasJwtToken) {
      try {
        // Try JWT decode - if it fails, it's an offline token
        jwtDecode(hasJwtToken);
      } catch (jwtError) {
        // This is an offline token, skip MSAL refresh
        return;
      }
    }

    if (
      !isAuthenticated &&
      !isLoading &&
      !pendingRef.current &&
      hasMsalAccount &&
      !hasJwtToken
    ) {
      pendingRef.current = true;
      instance
        .acquireTokenSilent(silentRequest)
        .then((accessToken) => {
          dispatch(
            loginWithAad({
              email: accessToken.account.username,
              token: accessToken.accessToken,
            }),
          ).then((result) => {
            pendingRef.current = false;
            if (result.type && result.type.endsWith("/rejected")) {
              // Backend hatası (500 vb.) → MSAL cache temizle, login ekranı görünsün
              console.warn("Backend login failed, clearing MSAL cache");
              instance.logoutRedirect({ onRedirectNavigate: () => false });
            }
          });
        })
        .catch((er) => {
          console.error("MSAL token acquisition failed:", er);
          pendingRef.current = false;
        });
    }
  }, [isAuthenticated, isLoading, instance, dispatch]);

  const location = useLocation();
  const fullscreenPaths = FULLSCREEN_ROUTES.map((r) => r.path);
  const isFullscreen = fullscreenPaths.includes(location.pathname);

  if (isLoading) {
    return <LoadingPanel />;
  }

  if (isFullscreen) {
    return (
      <ErrorBoundary>
        <Suspense fallback={<LoadingPanel />}>
          <Outlet />
        </Suspense>
      </ErrorBoundary>
    );
  }

  return (
    <ErrorBoundary>
      <Suspense fallback={<LoadingPanel />}>
        <Layout />
      </Suspense>
    </ErrorBoundary>
  );
};

/**
 * @type {import("react-router-dom").RouteObject[]}
 */
export const protectedRoutes = [
  {
    path: "/",
    element: <App />,
    children: [
      ...FEATURE_ROUTES.map(({ path, component: Component }) => ({
        path,
        element: <Component />,
      })),
      ...FULLSCREEN_ROUTES.map(({ path, component: Component }) => ({
        path,
        element: <Component />,
      })),
      { path: "*", element: <Navigate to="/" replace /> },
    ],
  },
];
